// SidebarShell — chrome-only left rail for the AppShell.
//
// Layout:
//   .sidebarHeader  — logo + Home + Search + QuickLinks (always visible).
//   .content        — module-injected nav items (CollectionsNav, SkipNav, ...).
//   .toggleRow      — collapse/expand toggle pinned to the bottom.
//
// All module-specific nav items live in their own components
// (e.g. CollectionsNav). Each module's layout route pushes its nav
// into the shell via `useSidebarContent()`; the shell renders whatever
// the SidebarContext currently holds. If no module has injected
// content, the rail is blank but still occupies its width.
//
// Decisions baked in:
//   - Collapse state lives in SidebarContext, persisted under
//     `tricivo:sidebar:collapsed`. Reading + persisting happens in
//     SidebarContext, not here.
//   - The shell publishes its current width to the
//     `--sidebar-current-width` CSS variable so AppShell's main
//     padding tracks the rail.

import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Home, Search } from 'lucide-react';
import { cn } from '../../utils/cn';
import { useSidebar } from './SidebarContext';
import { QuickLinksMenu } from './QuickLinksMenu';
import { NavSearch } from './NavSearch';
import styles from './Sidebar.module.css';

export interface SidebarProps {
  className?: string;
}

export function Sidebar({ className }: SidebarProps) {
  const { sidebarContent, isCollapsed, toggleCollapsed } = useSidebar();
  const location = useLocation();
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const isHomeActive =
    location.pathname === '/' || location.pathname === '/home';

  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.style.setProperty(
      '--sidebar-current-width',
      isCollapsed ? 'var(--sidebar-collapsed-width)' : 'var(--sidebar-width)',
    );
  }, [isCollapsed]);

  return (
    <>
      <nav
        className={cn(styles.sidebar, isCollapsed && styles.collapsed, className)}
        aria-label="Primary navigation"
      >
        <div className={styles.sidebarHeader}>
          <Link to="/" className={styles.brand} aria-label="Tricivo home">
            {isCollapsed ? (
              <span className={styles.brandMarkCompact} aria-hidden>
                T
              </span>
            ) : (
              <span className={styles.brandMark}>Tricivo</span>
            )}
          </Link>

          <div className={styles.headerActions}>
            <Link
              to="/"
              className={cn(
                styles.headerIcon,
                isHomeActive && styles.headerIconActive,
              )}
              aria-label="Home"
              aria-current={isHomeActive ? 'page' : undefined}
            >
              <Home size={18} aria-hidden />
            </Link>
            <button
              type="button"
              className={styles.headerIcon}
              aria-label="Search navigation"
              onClick={() => setIsSearchOpen(true)}
            >
              <Search size={18} aria-hidden />
            </button>
            <QuickLinksMenu />
          </div>
        </div>

        <div className={styles.content}>{sidebarContent}</div>

        <div className={styles.toggleRow}>
          <button
            type="button"
            className={styles.toggle}
            onClick={toggleCollapsed}
            aria-expanded={!isCollapsed}
            aria-label={isCollapsed ? 'Expand navigation' : 'Collapse navigation'}
          >
            {isCollapsed ? (
              <ChevronRight size={16} aria-hidden />
            ) : (
              <>
                <ChevronLeft size={16} aria-hidden />
                <span>Collapse</span>
              </>
            )}
          </button>
        </div>
      </nav>

      {isSearchOpen ? <NavSearch onClose={() => setIsSearchOpen(false)} /> : null}
    </>
  );
}
